import React from 'react';
import styled, { StyledComponent } from '@emotion/styled';
import View from './view';

const extractUrl = (source): string | undefined => {
  if (typeof source === 'string') {
    return source;
  }
  if (Array.isArray(source)) {
    return extractUrl(source[0]); // TODO: should pick the best url, based on screen size etc.
  }
  if (source && typeof source === 'object') {
    return source.uri;
  }
  return undefined;
};

type Size = {
  width?: string;
  height?: string;
};
const extractSize = (source): Size => {
  const isNumber = (n) => !Number.isNaN(parseFloat(n)) && Number.isFinite(n);

  if (Array.isArray(source)) {
    return extractSize(source[0]);
  }
  if (source && typeof source === 'object') {
    return {
      width: isNumber(source.width) ? `${source.width}px` : source.width,
      height: isNumber(source.height) ? `${source.height}px` : source.height,
    };
  }
  return {};
};

type ContainerProps = {
  size?: {
    width?: string;
    height?: string;
  };
};

const Container: StyledComponent<
  React.ComponentProps<typeof View> & ContainerProps
> = styled(View)((props: ContainerProps) => ({
  flexBasis: 'auto',
  overflow: 'hidden',
  zIndex: 0,
  ...(props.size ?? {}),
}));

//   `
//   flex-basis: auto;
//   overflow: hidden;
//   z-index: 0;
// `;

type BackgroundImageProps = {
  url?: string;
  cover?: boolean;
};

const BackgroundImage: StyledComponent<
  React.ComponentProps<typeof View> & BackgroundImageProps
> = styled(View)((props: BackgroundImageProps) => ({
  position: 'absolute' as const,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'transparent',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: props.cover ? 'cover' : 'auto',
  backgroundImage: props.url ? `url(${props.url})` : undefined,
  zIndex: -1,
}));

const AccessibilityImage = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  z-index: -1;
`;

type Source = {
  uri: string;
  width?: number | string;
  height?: number | string;
};
type Props = {
  inline?: boolean;
  source: string | Source | Source[];
  defaultSource?: string | Source | Source[];
  alt?: string;
  className?: string;
  cover?: boolean;
};

export default function Image({
  source,
  defaultSource,
  alt,
  className,
  inline = false,
  cover = true,
}: Props) {
  const url = extractUrl(source) || extractUrl(defaultSource);
  const size = extractSize(source);
  return (
    <Container size={{ ...size }} className={className}>
      <BackgroundImage inline={inline} url={url} cover={cover} />
      <AccessibilityImage src={url} alt={alt} />
    </Container>
  );
}
